import React, { useEffect, lazy, Suspense, useState } from 'react';
import * as api from '../../utils/api';
import { useParams, useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import parse from 'html-react-parser';
import LoadingSpinner from '../LoadingSpinner';
import * as MedlemsAdfaerdService from '@Utils/medlemsAdfaerdService';
import { TAllData, TAnswers, TChapters, TParams, TInput } from '../../utils/types';
import ButtonContainer from '../ButtonContainer';
import Grid from '../Grid';
import Modal from '../Modal';
import ConsentModal from '../../components/ConsentModal';
import SsoPensionsinfo from '@Components/ssoPensionsinfo/ssoPensionsinfo.component';
import FormattedText from '../FormattedText';
import { useGlobalState, setPenstionsinfoLoggedin } from '@Store/auth.store';

export interface ChapterProps {
  setAnswers: (answers: TAnswers[])=>void
  setNavParams: (params: TParams)=>void
  answers: TAnswers[]
  navParams: TParams
  resultUrl: string
  allData: TAllData
  y2answer?: boolean
  erMedlem?: boolean
  servicetype: string
}

const Page = lazy(() => import('../Page'));
const Graphics = lazy(() => import('../Graphics'));

const MotionGrid = motion(Grid);

const Chapter = ({ setNavParams, setAnswers, answers, navParams, resultUrl, allData, y2answer, erMedlem, servicetype }: ChapterProps) => {
	const { chapterId, pageId } = useParams<{chapterId: string, pageId: string}>();
	const history = useHistory();
	const [isPenstionsinfoLoggedin] = useGlobalState('isPenstionsinfoLoggedin');
	const fullData = allData.Chapters[Number(chapterId) - 1];
	const { EpiserverId } = fullData;
	const [data, setData] = useState<TChapters>();
	const [isLoading, setIsLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [modalData, setModalData] = useState({ modalHeader: '', modalText: '', modalBtnText1: '', modalBtnLink1: '', modalBtnText2: '', raadMail: '', raadBtn: '', pensionInfo: false, identitetsNoegle: '', pensionskasseNr: '', navn: '' });
	const { variant, chapterTitle } = navParams;
	const lastChapter = allData.Chapters.length;
	const [inputData, setInputData] = useState<TInput>();
	const [objdata, setObjData] = useState<any>();

	useEffect(() => {
		fetchChapter();
	}, [chapterId]);

	const fetchChapter = async () => {
		if (!chapterId) {
			return;
		}
		setIsLoading(true);
		const chapterData = servicetype === ('Velkomst' || 'Kontaktinformation') ? await api.getChapter(EpiserverId, true, servicetype) : await api.getChapter(EpiserverId, y2answer, servicetype);

		if (chapterData?.Pensionsinfodata && erMedlem){
			if (isPenstionsinfoLoggedin) {
				setPenstionsinfoLoggedin(false);
			}
			const response = await api.getPensionsinfoContent(EpiserverId, false, servicetype);

			setModalData({
				modalHeader: response.overskrift,
				modalText: response.tekst,
				modalBtnText1: response.hentPensionsinfoKnap,
				modalBtnLink1: response.linkTilPensionsInfo,
				modalBtnText2: '',
				raadMail: response.sendTilRaadgivningEmail,
				raadBtn: response.sendTilRaadgivningKnapTekst,
				pensionInfo: true,
				identitetsNoegle: response.identitetsNoegle,
				pensionskasseNr: response.pensionskasseNr,
				navn: response.navn
			});
		}

		if (chapterData?.IndhentSamtykke) {
			const objdata: { [key: string]: string } = {};
			const response = await api.getVelkomstContent(servicetype);
			response.CurrentBlock.forEach((e: { Name: string, Value: string }) => {
				const { Name, Value } = e;
				objdata[Name] = Value;
			});
			objdata['SamtykkeLabel'] = response.SamtykkeLabel;
			objdata['SamtykkeTekst'] = response.SamtykkeTekst;
			setObjData(objdata);

			setInputData({ email: response.Email, telephone: response.Telefon, consent: response.Samtykke });
		}

		setNavParams({ ...navParams, chapterTitle: chapterData.Title });
		setData(chapterData);
		setIsLoading(false);
	};

	const handleSubmit = (e: { preventDefault: () => void }) => {
		api.createEvent(EpiserverId, '');

		if (data?.IndhentSamtykke) {
			MedlemsAdfaerdService.gemInteraktion('SamtykkeSpringOver', objdata['BaseInteraktionDialogId'], '', 'WEB');
		}

		if (data.Sider[0]?.Content[0]?.Id) {
			setNavParams({ ...navParams, prev: `/kapitel/${Number(chapterId) - 1}/side/1`, variant: 'down' });
			e.preventDefault();
			history.push(`/kapitel/${chapterId}/side/1`);
		} else {
			setNavParams({ ...navParams, prev: `/kapitel/${Number(chapterId) - 1}/snack`, variant: 'down' });
			e.preventDefault();
			Number(chapterId) !== lastChapter ? history.push(`/kapitel/${chapterId}/snack`) : ((servicetype === 'Velkomst' || servicetype === 'Kontaktinformation') ? history.push('/anbefalinger') : window.location.href = resultUrl);
		}
	};

	const handleModalClick = () => {
		if (modalData || data?.IndhentSamtykke) {
			api.createEvent(EpiserverId, '');
			setShowModal(true);
		}
	};

	const handleModalVisibility = () => {
		setShowModal(false);
		history.push(`${chapterId}/side/1/snack`);
	};

	useEffect(() => {

		if (Number(chapterId) === 1 && !pageId) {
			setNavParams({
				chapter: chapterId,
				chapterTitle,
				page: '0',
				show: true,
				prev: '/',
				variant: variant
			});
		}

		if (Number(chapterId) !== 1 && (!pageId || pageId === '0' || !navParams.prev)) {
			setNavParams({
				chapter: chapterId,
				chapterTitle,
				page: '0',
				show: true,
				prev: navParams.prev ? navParams.prev : `/kapitel/${Number(chapterId) - 1}/side/1`,
				variant: variant
			});
		}
	}, [chapterId, pageId]);

	useEffect(() => {
		if (modalData?.pensionInfo && isPenstionsinfoLoggedin) {
			setAnswers([]);
			history.push(`${chapterId}/side/1/snack`);
		}
	}, [isPenstionsinfoLoggedin]);

	if (isLoading || !data) {
		return <LoadingSpinner />;
	}

	return (
		<Suspense fallback={ <LoadingSpinner /> }>
			{ !pageId && (
				<MotionGrid
					className="page chapter"
					mobileBlock
					initial={ { opacity: 0, x: 0, y: navParams.variant === 'down' ? 700 : -700 } }
					animate={ { opacity: 1, x: 0, y: 0 } }
					exit={ { opacity: 0, x: 0, y: navParams.variant === 'down' ? -700 : 700 } }
					transition={ { duration: 0.5, ease: 'easeIn' } }
				>
					{ data.Graphic?.Type && (
						<Grid.Element position="right" type={ data.Graphic.Type.toLowerCase() || '' }>
							<Graphics data={ data.Graphic } />
						</Grid.Element>
					) }

					<Grid.Element position="left" type="text">
						<div className="chapter-content">
							<h2 className="page-header"><span>{ chapterId }</span>{ data.Title }</h2>
							<span>{ data.Intro && parse(data.Intro) }</span>
							<ButtonContainer flex>
								<button className="btn" onClick={ (data?.Pensionsinfodata && erMedlem) || data?.IndhentSamtykke ? handleModalClick : handleSubmit }>{ data.ButtonText }</button>
								{ data.Button2Text &&
									<button className="btn btn-secondary" onClick={ handleSubmit }>{ data.Button2Text }</button> }
							</ButtonContainer>
							<motion.button
								type="submit"
								className="btn-nav-down"
								onClick={ handleSubmit }
								initial={ { opacity: 0 } }
								animate={ { opacity: 1 } }
								transition={ { duration: 1.5, ease: 'easeIn' } }
							>
								<svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
									<circle cx="21" cy="21" r="20.5" transform="rotate(90 21 21)" fill="white" stroke="#D8D4D7" />
									<path d="M28.0044 19.0735L20.9333 26.1446L13.8623 19.0735" stroke="#92004D" strokeWidth="2" />
								</svg>
							</motion.button>
						</div>
					</Grid.Element>

				</MotionGrid>
			) }
			{ showModal && (
				<Modal
					onClose={ ()=>{
						setShowModal(false);
					} }
				>
					{ modalData?.modalHeader && <h3 className="format-as-h3" style={ { marginBottom: '1.25rem' } }>{ modalData.modalHeader }</h3> }
					{ modalData?.modalText && <FormattedText formatClass="format-as-body" formatLinks={ true }>{ parse(modalData.modalText) }</FormattedText> }
					<ButtonContainer>
						{ modalData?.pensionInfo && erMedlem && (
							<SsoPensionsinfo
								classBlock={ 'nyipka' }
								currentBlock={ {
									pensionsinfoKnapLabel: modalData?.modalBtnText1,
									pensionsinfoTekst: null,
									pensionsinfoFejlTekst: 'FEJL I PENSIONSTJEK KALD',
									sendTilRaadgivningKnapTekst: modalData?.raadBtn,
									sendTilRaadgivningEmail: modalData?.raadMail
								} }
								linkTilPensionsInfo={ modalData?.modalBtnLink1 }
								identitetsNoegle={ modalData.identitetsNoegle }
								pensionskasseNr={ modalData.pensionskasseNr }
								navn={ modalData.navn }
							/>
						) }
						{ modalData?.modalBtnText1 && !modalData?.pensionInfo && (
							<button
								className="btn"
								onClick={ ()=>{
									setShowModal(false);
								} }>{ modalData?.modalBtnText1 }</button>
						) }
						{ modalData?.modalBtnText2 && <button className="btn btn-secondary" onClick={ handleSubmit }>{ modalData?.modalBtnText2 }</button> }
					</ButtonContainer>
				</Modal>
			) }
			{ showModal && data?.IndhentSamtykke && (
				<ConsentModal
					showConsent={ true }
					showEmailonlyForm={ false }
					modalData={ modalData }
					inputData={ inputData }
					handleModalVisibility={ handleModalVisibility }
					setShowModal={ setShowModal }
					samtykkeTekst={ objdata['SamtykkeTekst'] }
					samtykkeLabel={ objdata['SamtykkeLabel'] }
					objdata={ objdata }
				/>
			) }
			{ data && data.Sider && data?.Sider.map((e, i: React.Key) => {
				return (
					<Page
						key={ i }
						id={ Number(i) }
						setAnswers={ setAnswers }
						setNavParams={ setNavParams }
						navParams={ navParams }
						answers={ answers }
						tooltipData={ e.Tooltip }
						buttonText={ 'Næste' }
						content={ e.Content }
						chapterData={ data }
						lastChapter={ lastChapter }
						servicetype={ servicetype }
						resultUrl={ resultUrl }
					/>
				);
			}) }
		</Suspense>
	);
};

export default Chapter;